"use client";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";


export default function useBatchCardAnimation({
  index = 0,
  isParentInView,
  isFirstInBatch = false,
  batchIndex,
  onBatchEnter,
}) {
  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  const { ready, getTransitionReady, isRouteChanging, isMobile, currentTransitionName } = useApp();
  const isParentInViewRef = useRef(false);
  const [isIn, setIsIn] = useState(false);
  const skipAnimationRef = useRef(currentTransitionName === "overlay-out");

  // Clear skip flag after scroll restoration has settled
  useEffect(() => {
    if (skipAnimationRef.current) {
      const timer = setTimeout(() => {
        skipAnimationRef.current = false;
      }, 300);
      return () => clearTimeout(timer);
    }
  }, []);

  const animateIn = async () => {
    await ready;
    await getTransitionReady();

    if (!rootRef.current) return;

    const delay = batchIndex === 0 ? 0.3 : 0;
    const stagger = batchIndex === 0 ? 0.1 : 0.05;

    gsap.to(rootRef.current, {
      opacity: 1,
      duration: 0.6,
      ease: 'linear.none',
      delay: delay + index * stagger,
    });


    gsap.to(rootRef.current, {
      y: "0%",
      duration: 1.2,
      ease: "expo.out",
      delay: delay + index * stagger,
    });
  };

  // ScrollTrigger for first card in batch to notify parent
  useGSAP(
    () => {
      if (!isFirstInBatch || !scrollRef.current) return;

      ScrollTrigger.create({
        trigger: scrollRef.current,
        start: isMobile ? 'top bottom' : "top bottom+=10%",
        once: true,
        onEnter: () => {
          onBatchEnter?.(batchIndex);
        },
      });
    },
    {
      dependencies: [ready, isRouteChanging, isFirstInBatch, batchIndex, onBatchEnter, isMobile],
      revertOnUpdate: true,
    }
  );

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      mm.add(
        {
          hasMotion: "(prefers-reduced-motion: no-preference)",
          hasNoMotion: "(prefers-reduced-motion: reduce)",
        },
        (context) => {
          const { hasMotion, hasNoMotion } = context.conditions;

          if (hasMotion) {
            if (skipAnimationRef.current && isParentInView) {
              // Returning from story detail — cards already in viewport show immediately
              if (rootRef.current) {
                gsap.set(rootRef.current, { opacity: 1, y: "0%" });
              }
              isParentInViewRef.current = true;
              setIsIn(true);
            } else {
              if (rootRef.current) {
                gsap.set(rootRef.current, {
                  y: "50%",
                });
              }

              if (
                rootRef.current &&
                isParentInView &&
                !isParentInViewRef.current
              ) {
                isParentInViewRef.current = true;
                setIsIn(true);
                animateIn();
              }
            }
          } else if (hasNoMotion) {
            if (rootRef.current) {
              gsap.set(rootRef.current, {
                opacity: 1,
                y: "0%",
              });
            }
          }
        }
      )
    },
    [ready, getTransitionReady, isParentInView]
  );

  return { rootRef, scrollRef, isIn };
}
